'use client';

import React from 'react';

import { Button, Card, Heading, Input } from '@ui';
import { Container, Section, SectionHeader } from '@layout';
import { withOptionalId } from '@lib/withOptionalId';
import { t as translate } from '@utils/translate';

import FoundationsStrip from './FoundationsStrip';

const PRIMARY_RAMP = [
  '--brand-50',
  '--brand-100',
  '--brand-200',
  '--brand-300',
  '--brand-400',
  '--brand-500',
  '--brand-600',
  '--brand-700',
  '--brand-800',
  '--brand-900',
] as const;

const SECONDARY_RAMP = [
  '--secondary-50',
  '--secondary-100',
  '--secondary-200',
  '--secondary-300',
  '--secondary-400',
  '--secondary-500',
  '--secondary-600',
  '--secondary-700',
  '--secondary-800',
  '--secondary-900',
] as const;

const SEMANTIC_SWATCHES = ['--success', '--warning', '--danger', '--info'] as const;

const SURFACE_SWATCHES = [
  '--surface-0',
  '--surface-1',
  '--surface-2',
  '--surface-3',
  '--surface-4',
  '--surface-5',
  '--surface-6',
  '--surface-7',
  '--surface-8',
] as const;

const SHADOW_TOKENS = ['--shadow-sm', '--shadow-md', '--shadow-lg'] as const;

interface DesignSystemOverviewProps {
  sectionID?: string;
  classes?: string;
  isSpanish?: boolean;
}

function SwatchRow({ tokens, label }: { tokens: readonly string[]; label?: string }) {
  return (
    <div className='swatch-block'>
      {label ? <p className='label'>{label}</p> : null}
      <div className='swatch-grid'>
        {tokens.map((token) => (
          <div key={token} className='swatch'>
            <div className='swatch-chip' style={{ background: `var(${token})` }} />
            <div className='swatch-meta'>{token}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DesignSystemOverview: React.FC<DesignSystemOverviewProps> = ({
  sectionID = 'design-system-overview',
  classes = '',
  isSpanish = false,
}) => {
  const locale = isSpanish ? 'es' : 'en';

  const tStr = (fullKey: string, fallback: string) => {
    const v = translate(fullKey, 'design-system', locale);
    if (typeof v === 'string' && v !== fullKey && v.trim()) return v;
    return fallback;
  };

  const hColor = tStr('ui_kit_page.overview.h_color', 'Color system');
  const hPrimary = tStr('ui_kit_page.overview.h_primary_ramp', 'Primary (brand ramp)');
  const hSecondary = tStr('ui_kit_page.overview.h_secondary_ramp', 'Secondary (violet ramp)');
  const hSemantic = tStr('ui_kit_page.overview.h_semantic', 'Semantic');
  const hType = tStr('ui_kit_page.overview.h_typography', 'Typography');
  const hSurfaces = tStr('ui_kit_page.overview.h_surfaces', 'Surfaces & elevation');
  const surfacesNote = tStr(
    'ui_kit_page.overview.surfaces_note',
    'Surface steps stack from page (--surface-0) through raised cards; elevation uses shared shadow tokens. Toggle light/dark in the header to verify contrast.'
  );
  const hSpacing = tStr('ui_kit_page.overview.h_spacing', 'Spacing & radius');
  const hMicro = tStr('ui_kit_page.overview.h_micro_pattern', 'In practice');
  const microIntro = tStr(
    'ui_kit_page.overview.micro_intro',
    'The same tokens compose into predictable UI: surface, label, field, and action working together.'
  );

  return (
    <Section {...withOptionalId(sectionID)} className={`design-system-overview ${classes}`.trim()}>
      <Container>
        <div className='subsection' id='overview-micro-ui'>
          <Card header={{ title: hMicro, description: microIntro, as: 'h3' }}>
            <Card className='micro-card'>
              <form
                className='micro-form'
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <Input
                  id='overview-micro-email'
                  type='email'
                  label={tStr('ui_kit_page.overview.micro_label', 'Work email')}
                  placeholder='you@team.com'
                  autoComplete='email'
                />
                <div className='row btn-row gap' style={{ flexWrap: 'wrap' }}>
                  <Button
                    type='submit'
                    label={tStr('ui_kit_page.overview.micro_cta', 'Continue')}
                    className='btn-primary'
                  />
                </div>
              </form>
            </Card>
          </Card>
        </div>

        <div className='subsection' id='overview-color'>
          <SectionHeader title={hColor} as='h3' />
          <SwatchRow tokens={PRIMARY_RAMP} label={hPrimary} />
          <SwatchRow tokens={SECONDARY_RAMP} label={hSecondary} />
          <SwatchRow tokens={SEMANTIC_SWATCHES} label={hSemantic} />
        </div>

        <div className='subsection' id='overview-typography'>
          <SectionHeader title={hType} as='h3' />
          <div className='type-showcase surface-1'>
            <span className='h1 type-sample'>Heading 1</span>
            <span className='h2 type-sample'>Heading 2</span>
            <span className='h3 type-sample'>Heading 3</span>
            <p className='type-sample'>
              {tStr(
                'ui_kit_page.overview.body_sample',
                'Body text uses the default size and line height for long-form reading and UI copy.'
              )}
            </p>
            <p className='small type-sample'>
              {tStr(
                'ui_kit_page.overview.small_sample',
                'Small text for captions, hints, and secondary metadata.'
              )}
            </p>
            <code className='code-sample'>const primary = &apos;var(--brand-700)&apos;;</code>
          </div>
        </div>

        <div className='subsection' id='overview-surfaces'>
          <SectionHeader title={hSurfaces} description={surfacesNote} as='h3' />
          <SwatchRow tokens={SURFACE_SWATCHES} />
          <div className='elevation-row'>
            {SHADOW_TOKENS.map((token) => (
              <div key={token} className='elev-item'>
                <span className='elev-token'>{token}</span>
                <div className='elev-box surface-1' style={{ boxShadow: `var(${token})` }} />
              </div>
            ))}
          </div>
        </div>

        <div className='subsection' id='overview-spacing'>
          <SectionHeader title={hSpacing} as='h3' />
          <FoundationsStrip
            embedded
            sectionID='foundations-tokens'
            omitElevation
            isSpanish={isSpanish}
          />
        </div>
      </Container>
    </Section>
  );
};

export default DesignSystemOverview;
